const DEFAULT_DELAY_MS = 1200
const DEFAULT_MAX_RETRIES = 3
const DEFAULT_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 15000
const MAX_JITTER_MS = 400

let limiterQueue: Promise<void> = Promise.resolve()
let nextAvailableSlotAt = 0

/**
 * Delays execution for the specified amount of time
 * @param ms - Milliseconds to wait
 */
export const delay = (ms: number = DEFAULT_DELAY_MS): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function reserveGlobalSlot(minIntervalMs: number): Promise<void> {
  const reservation = limiterQueue.then(async () => {
    const now = Date.now()
    const slotAt = Math.max(now, nextAvailableSlotAt)
    nextAvailableSlotAt = slotAt + minIntervalMs

    const waitMs = slotAt - now
    if (waitMs > 0) {
      await delay(waitMs)
    }
  })

  limiterQueue = reservation.catch(() => undefined)
  return reservation
}

function getHeader(headers: any, key: string) {
  if (!headers) return undefined

  if (typeof headers.get === 'function') {
    return headers.get(key) ?? headers.get(key.toLowerCase())
  }

  const normalizedKey = key.toLowerCase()
  const entry = Object.entries(headers).find(([headerKey]) => headerKey.toLowerCase() === normalizedKey)
  return entry?.[1]
}

function parseRetryAfterDelayMs(error: any): number | null {
  const headers = error?.response?.headers

  const retryAfter = getHeader(headers, 'retry-after')
  if (retryAfter) {
    const retryAfterValue = Array.isArray(retryAfter) ? retryAfter[0] : retryAfter
    const asSeconds = Number(retryAfterValue)

    if (Number.isFinite(asSeconds)) {
      return Math.max(0, Math.round(asSeconds * 1000))
    }

    const asDateMs = new Date(retryAfterValue).getTime()
    if (Number.isFinite(asDateMs)) {
      return Math.max(0, asDateMs - Date.now())
    }
  }

  const resetCandidates = [
    getHeader(headers, 'x-ratelimit-reset-ms'),
    getHeader(headers, 'x-ratelimit-reset'),
    getHeader(headers, 'x-rate-limit-reset'),
    getHeader(headers, 'x-ratelimit-reset-after'),
  ]

  for (const candidate of resetCandidates) {
    if (candidate == null) continue
    const value = Array.isArray(candidate) ? candidate[0] : candidate
    const numericValue = Number(value)

    if (!Number.isFinite(numericValue) || numericValue <= 0) continue

    // Typical conventions:
    // - reset-after: seconds
    // - reset: unix timestamp (seconds) or epoch milliseconds
    if (String(value).includes('.')) {
      return Math.max(0, Math.round(numericValue * 1000))
    }

    if (numericValue > 1_000_000_000_000) {
      return Math.max(0, Math.round(numericValue - Date.now()))
    }

    if (numericValue > 1_000_000_000) {
      return Math.max(0, Math.round((numericValue * 1000) - Date.now()))
    }

    return Math.max(0, Math.round(numericValue * 1000))
  }

  return null
}

function isTransientError(error: any): boolean {
  const status = error?.status ?? error?.response?.status
  if (status === 429) return true
  if (status >= 500 && status <= 599) return true

  const code = String(error?.code || '').toUpperCase()
  const transientCodes = new Set([
    'ECONNABORTED',
    'ECONNRESET',
    'ETIMEDOUT',
    'ERR_NETWORK',
    'ENOTFOUND',
    'EAI_AGAIN',
    'EPIPE',
  ])

  return transientCodes.has(code)
}

/**
 * Executes an async function with rate limiting
 * @param fn - Async function to execute
 * @param delayMs - Delay in milliseconds between calls (default: 1200ms)
 * @returns Result of the function
 */
export async function withRateLimit<T>(
  fn: () => Promise<T>,
  delayMs: number = DEFAULT_DELAY_MS
): Promise<T> {
  await reserveGlobalSlot(delayMs)
  return fn()
}

/**
 * Executes an async function with retry logic for transient failures.
 * @param fn - Async function to execute
 * @param maxRetries - Maximum number of retries (default: 3)
 * @param retryDelay - Base retry delay in milliseconds (default: 1000ms)
 * @returns Result of the function or throws error after retries exhausted
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = DEFAULT_MAX_RETRIES,
  retryDelay: number = DEFAULT_RETRY_DELAY_MS
): Promise<T> {
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error: any) {
      lastError = error
      const status = error?.status ?? error?.response?.status
      const shouldRetry = isTransientError(error)

      if (!shouldRetry || attempt >= maxRetries) {
        throw error
      }

      const serverDelay = status === 429 ? parseRetryAfterDelayMs(error) : null
      const expDelay = Math.min(retryDelay * (2 ** attempt), MAX_RETRY_DELAY_MS)
      const jitter = Math.floor(Math.random() * MAX_JITTER_MS)
      const waitMs = Math.max(serverDelay ?? 0, expDelay + jitter)

      console.warn(`[rateLimiter] transient error (${status ?? 'unknown'}), retrying in ${waitMs}ms...`)
      await delay(waitMs)
    }
  }

  throw lastError
}
